import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'gatsby';

import Logo from '@src/svgs/Logo';

import * as styles from './footer.module.scss';

interface FooterProps {
  /** When the site as a whole was last built (presumably from the field of the same name from Gatsby site metadata). */
  lastBuildTime: Date;
  /** When the content on this page was last updated. */
  lastUpdatedDate?: string;
}

const Footer: React.FC<FooterProps> = ({lastBuildTime, lastUpdatedDate}) => {
  const {i18n, t} = useTranslation('Footer');

  const now = lastBuildTime;

  // Guides available only on weekdays from April through September
  const thisMonth = now.getUTCMonth();
  const representativeHours =
    thisMonth >= 3 && thisMonth <= 8
      ? t('representativeHoursWeekdays')
      : t('representativeHoursEveryDay');

  return (
    <footer className={styles.footer}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div className={styles.info}>
            <div className={styles.logo}>
              <Link className={styles.link} to={t('links.home.url')}>
                <span className={styles.visuallyHidden}>
                  {t('links.home.text')}
                </span>
                <Logo />
              </Link>
            </div>
          </div>
          <div className={styles.links}>
            <div className={styles.linkGroup}>
              <h3 className={styles.heading}>{t('links.learnMore.heading')}</h3>
              <ul className={styles.list}>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.prescriptionDrugs.url')}>
                    {t('links.learnMore.prescriptionDrugs.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.healthWellness.url')}>
                    {t('links.learnMore.healthWellness.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.salesEvents.url')}>
                    {t('links.learnMore.salesEvents.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.memberEvents.url')}>
                    {t('links.learnMore.memberEvents.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.medicare.url')}>
                    {t('links.learnMore.medicare.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.providers.url')}
                    onClick={() => {
                      i18n.changeLanguage('en-us');
                    }}>
                    {t('links.learnMore.providers.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.brokers.url')}
                    onClick={() => {
                      i18n.changeLanguage('en-us');
                    }}>
                    {t('links.learnMore.brokers.text')}
                  </Link>
                </li>

                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.learnMore.developers.url')}
                    onClick={() => {
                      i18n.changeLanguage('en-us');
                    }}>
                    {t('links.learnMore.developers.text')}
                  </Link>
                </li>
              </ul>
            </div>
            <div className={styles.linkGroup}>
              <h3 className={styles.heading}>{t('links.about.heading')}</h3>
              <ul className={styles.list}>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.ourStory.url')}>
                    {t('links.about.ourStory.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.ourTechnology.url')}>
                    {t('links.about.ourTechnology.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.ourTeam.url')}>
                    {t('links.about.ourTeam.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.ourServiceArea.url')}>
                    {t('links.about.ourServiceArea.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.newsroom.url')}>
                    {t('links.about.newsroom.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <a
                    className={styles.link}
                    href={t('links.about.careers.url')}>
                    {t('links.about.careers.text')}
                  </a>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.about.contactUs.url')}>
                    {t('links.about.contactUs.text')}
                  </Link>
                </li>
              </ul>
            </div>
            <div className={styles.linkGroup}>
              <h3 className={styles.heading}>{t('links.policies.heading')}</h3>
              <ul className={styles.list}>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.policies.termsOfUse.url')}>
                    {t('links.policies.termsOfUse.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.policies.privacyPolicy.url')}>
                    {t('links.policies.privacyPolicy.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.policies.nonDiscriminationNotice.url')}>
                    {t('links.policies.nonDiscriminationNotice.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.policies.accessibility.url')}>
                    {t('links.policies.accessibility.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t('links.policies.qualityImprovementStrategy.url')}>
                    {t('links.policies.qualityImprovementStrategy.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <Link
                    className={styles.link}
                    to={t(
                      'links.policies.memberRightsAndResponsibilities.url',
                    )}>
                    {t('links.policies.memberRightsAndResponsibilities.text')}
                  </Link>
                </li>
                <li className={styles.listItem}>
                  <a
                    className={styles.link}
                    href={t('links.policies.compliance.url')}>
                    {t('links.policies.compliance.text')}
                  </a>
                </li>
                <li className={styles.listItem}>
                  <a
                    className={styles.link}
                    href={t('links.policies.codeOfConduct.url')}
                    rel="noreferrer"
                    target="_blank">
                    {t('links.policies.codeOfConduct.text')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.utility}>
          <ul className={styles.utilityList}>
            <li className={styles.utilityItem}>
              <span className={styles.utilityTitle}>{representativeHours}</span>
            </li>
            <li className={styles.utilityItem}>
              <span className={styles.utilitySubTitle}>
                {t('smsText')}{' '}
                <span className={styles.phoneNumber}>866–85</span>
              </span>
              <span className={styles.utilitySubTitle}>
                {t('phoneText')}{' '}
                <a className={styles.phoneNumber} href="tel:1-800-338-6833">
                  1-800-DEVOTED <span>(338–6833) TTY 711</span>
                </a>
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div className={styles.disclosure}>
        <div className={styles.wrapper}>
          <div className={styles.disclosureContent}>
            <div className={styles.disclosureCols}>
              <p>
                <a href={t('disclaimers.url')}>{t('disclaimers.label')}</a>
              </p>
            </div>
            <div className={styles.bottomContent}>
              <p className={styles.cmsNumber}>{t('cmsNumber')}</p>
              <div className={styles.copyright}>
                <p className={styles.copyrightText}>
                  © {now.getUTCFullYear()} Devoted Health, Inc.
                </p>
                <p className={styles.spacer}>|</p>
                {lastUpdatedDate && (
                  <p className={styles.lastUpdated}>
                    {t('lastUpdated')} {lastUpdatedDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
